/* eslint-disable jsx-a11y/anchor-is-valid */
import {axiosIns} from 'config/api.config'
import {ENV} from 'config/ENV.config'
import React, {FC, useEffect, useState} from 'react'
import {KTSVG} from '_metronic/helpers'

type PurchasedModule = {
  id: string
  title: string
  code: string
  appKey?: string
  hostOrigin?: string
}

/** One cell of the 9-dot grid. `url` is empty for the app you are already in. */
type ModuleTile = {
  id: string
  title: string
  subtitle: string
  icon: string
  url: string
  isCurrent: boolean
}

// Per-app icon; any app not listed falls back to a generic one, so new apps still render.
const MODULE_ICONS: Record<string, string> = {
  ERPBOI_STORE: '/media/icons/duotune/finance/fin007.svg',
  ERPBOI_AMS: '/media/icons/duotune/finance/fin001.svg',
  ERPBOI_HRM: '/media/icons/duotune/communication/com014.svg',
  ERPBOI_ITAM: '/media/icons/duotune/technology/teh001.svg',
  ERPBOI_DOCS: '/media/icons/duotune/files/fil012.svg',
  ERPBOI_DESK: '/media/icons/duotune/communication/com007.svg',
  ERPBOI_LABOR: '/media/icons/duotune/communication/com006.svg',
  ERPBOI_MERCHANDISING: '/media/icons/duotune/general/gen017.svg',
  ERPBOI_PORTAL: '/media/icons/duotune/general/gen051.svg',
}
const DEFAULT_ICON = '/media/icons/duotune/finance/fin006.svg'

/** Portal's app key. It is never a purchasable module, so it never comes back from the endpoint. */
const PORTAL_APP_KEY = 'ERPBOI_PORTAL'
/** Portal is the suite's administration console, and is labelled as such in the switcher. */
const PORTAL_TITLE = 'Administration'
/** Portal's console landing page (its `@constants/internal-route.constant` DASHBOARD). */
const PORTAL_PATH = '/dashboard'

const toUrl = (host?: string) => (!host ? '' : /^https?:\/\//i.test(host) ? host : `https://${host}`)

// The grid is the whole suite as this company sees it: the apps it has purchased (portal
// `public.company_modules`, active + `is_visible=true`), the app you are currently in — listed
// but DISABLED, so the switcher shows where you are rather than silently omitting it — and
// Administration, the Portal console. Everything but Administration is company-wise and dynamic:
// a newly-purchased app shows up on its own, nothing is hardcoded.
const buildTiles = (list: PurchasedModule[]): ModuleTile[] => {
  // A module with no host to open is only useful as the "you are here" tile.
  const tiles: ModuleTile[] = list
    .filter((m) => m.hostOrigin || m.appKey === ENV.appCode)
    .map((m) => {
      const isCurrent = m.appKey === ENV.appCode
      return {
        id: m.id,
        title: m.title,
        subtitle: isCurrent ? 'Current module' : 'Module',
        icon: MODULE_ICONS[m.appKey || ''] || DEFAULT_ICON,
        url: isCurrent ? '' : toUrl(m.hostOrigin),
        isCurrent,
      }
    })

  // Derived from REACT_APP_PORTAL_URL, never a literal: that variable points at localhost in dev,
  // so a hardcoded portal host here would be unreachable there. Unset => no tile rather than a
  // link relative to this app's own origin, which would 404 inside the current module.
  if (ENV.portalOrigin) {
    tiles.push({
      id: PORTAL_APP_KEY,
      title: PORTAL_TITLE,
      subtitle: 'Console',
      icon: MODULE_ICONS[PORTAL_APP_KEY],
      url: `${ENV.portalOrigin}${PORTAL_PATH}`,
      isCurrent: false,
    })
  }

  return tiles
}

const cellClass = (index: number) =>
  'd-flex flex-column flex-center h-100 p-6 border-bottom' + (index % 2 === 0 ? ' border-end' : '')

// Declared at module scope: a component defined inside the render body is a new type on every
// render, which remounts the cell.
const TileBody: FC<{tile: ModuleTile}> = ({tile}) => (
  <>
    <KTSVG path={tile.icon} className='svg-icon-3x svg-icon-primary mb-2' />
    <span className='fs-5 fw-bold text-gray-800 mb-0 text-center'>{tile.title}</span>
    <span className='fs-7 text-gray-400 text-center'>{tile.subtitle}</span>
  </>
)

const QuickLinks: FC = () => {
  const [tiles, setTiles] = useState<ModuleTile[]>([])
  const [loading, setLoading] = useState<boolean>(true)

  useEffect(() => {
    axiosIns
      .get('/module/get-purchased-modules', {baseURL: ENV.portalGetway})
      .then((res: any) => setTiles(buildTiles(res?.body || [])))
      // A module-list outage must still leave Administration reachable.
      .catch(() => setTiles(buildTiles([])))
      .finally(() => setLoading(false))
  }, [])

  return (
    <div
      className='menu menu-sub menu-sub-dropdown menu-column w-250px w-lg-325px'
      data-kt-menu='true'
    >
      {loading ? (
        <div className='py-8 text-center text-gray-500'>Loading…</div>
      ) : tiles.length === 0 ? (
        <div className='py-8 px-6 text-center text-gray-500'>No modules available.</div>
      ) : (
        <div className='row g-0'>
          {tiles.map((tile, i) => (
            <div className='col-6' key={tile.id}>
              {tile.isCurrent ? (
                // Rendered as a plain cell, dimmed and unclickable: a switcher offering to switch
                // to where you already are reads as a dead link.
                <div
                  className={cellClass(i) + ' opacity-50'}
                  style={{cursor: 'default'}}
                  aria-disabled='true'
                  title='You are here'
                >
                  <TileBody tile={tile} />
                </div>
              ) : (
                <a
                  href={tile.url}
                  target='_blank'
                  rel='noreferrer'
                  className={cellClass(i) + ' bg-hover-light'}
                >
                  <TileBody tile={tile} />
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export {QuickLinks}
